import React, { useEffect, useState, useCallback } from 'react';
import { Flag, Plus, Eye, CheckCircle2, FlaskConical, XCircle, Trash2, Copy, Check } from 'lucide-react';
import {
  listFeatureFlags,
  createFeatureFlag,
  updateFeatureFlagStatus,
  updateFeatureFlag,
  deleteFeatureFlag,
  evaluateFeatureFlag,
} from '../services/featureFlagService';
import { fetchAuditLogs } from '../services/systemAuditLogService';
import { GridPageShell } from '@ui-admin/components/GridPageShell';
import type { DataGridColumn, DataGridFilter, DataGridRowAction, GridTemplate } from '@ui-admin/components/DataGrid';
import ConfirmDeleteModal from '../components/ui/ConfirmDeleteModal';
import ConfirmActionModal from '../components/ui/ConfirmActionModal';
import AuditLogTimeline from '@ui-admin/components/AuditLogTimeline';

// ============================================================
// Types
// ============================================================

interface FeatureFlag {
  _id: string;
  key: string;
  name: string;
  category: string;
  description?: string;
  minTier: string | null;
  status: 'enabled' | 'disabled' | 'beta';
  defaultValue: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// ============================================================
// Display constants
// ============================================================

const STATUS_COLORS: Record<string, string> = {
  enabled: 'bg-green-100 text-green-700',
  disabled: 'bg-gray-100 text-gray-600',
  beta: 'bg-yellow-100 text-yellow-700',
};

const TIER_COLORS: Record<string, string> = {
  professional: 'bg-blue-100 text-blue-700',
  advanced: 'bg-purple-100 text-purple-700',
  premium: 'bg-amber-100 text-amber-700',
};

// ============================================================
// COLUMNS
// ============================================================

const COLUMNS: DataGridColumn<FeatureFlag>[] = [
  {
    id: 'key',
    header: 'Key',
    accessorKey: 'key',
    sortable: true,
    filterable: true,
    minWidth: 220,
    cell: (info) => {
      const row = info.row.original;
      return (
        <div className="flex items-center gap-2">
          <div className="shrink-0 h-7 w-7 rounded-md bg-orange-50 flex items-center justify-center">
            <Flag className="h-3.5 w-3.5 text-orange-500" />
          </div>
          <span className="font-mono text-sm text-gray-900 truncate">{row.key}</span>
        </div>
      );
    },
  },
  {
    id: 'name',
    header: 'Name',
    accessorKey: 'name',
    sortable: true,
    filterable: true,
    minWidth: 160,
    cell: (info) => <span className="text-sm text-gray-700">{info.getValue() as string}</span>,
  },
  {
    id: 'category',
    header: 'Category',
    accessorKey: 'category',
    sortable: true,
    filterable: true,
    width: 120,
    cell: (info) => {
      const v = info.getValue() as string;
      return v
        ? <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700 capitalize">{v}</span>
        : <span className="text-gray-300">—</span>;
    },
  },
  {
    id: 'minTier',
    header: 'Min Tier',
    accessorKey: 'minTier',
    sortable: true,
    width: 120,
    cell: (info) => {
      const v = info.getValue() as string | null;
      if (!v) return <span className="text-sm text-gray-400">All Tiers</span>;
      return (
        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full capitalize ${TIER_COLORS[v] || 'bg-gray-100 text-gray-600'}`}>
          {v}
        </span>
      );
    },
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    sortable: true,
    filterable: true,
    width: 100,
    cell: (info) => {
      const v = info.getValue() as string;
      return (
        <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full capitalize ${STATUS_COLORS[v] || 'bg-gray-100 text-gray-600'}`}>
          {v}
        </span>
      );
    },
  },
  {
    id: 'defaultValue',
    header: 'Default',
    accessorKey: 'defaultValue',
    sortable: true,
    width: 90,
    cell: (info) => {
      const v = info.getValue() as boolean;
      return <span className={`text-sm font-medium ${v ? 'text-green-600' : 'text-gray-400'}`}>{v ? '✓ On' : '✗ Off'}</span>;
    },
  },
  {
    id: 'updatedAt',
    header: 'Last Updated',
    accessorKey: 'updatedAt',
    sortable: true,
    width: 130,
    hidden: true,
    cell: (info) => {
      const v = info.getValue() as string;
      return v
        ? <span className="text-sm text-gray-500">{new Date(v).toLocaleDateString()}</span>
        : <span className="text-gray-300 text-sm">—</span>;
    },
  },
];

// ============================================================
// FILTERS
// ============================================================

const FILTERS: DataGridFilter[] = [
  {
    id: 'status',
    label: 'Status',
    type: 'status',
    options: [
      { value: 'enabled', label: 'Enabled', color: 'green' },
      { value: 'disabled', label: 'Disabled', color: 'gray' },
      { value: 'beta', label: 'Beta', color: 'yellow' },
    ],
  },
  {
    id: 'category',
    label: 'Category',
    type: 'select',
    options: [
      { value: 'core', label: 'Core' },
      { value: 'ai', label: 'AI' },
      { value: 'reports', label: 'Reports' },
      { value: 'billing', label: 'Billing' },
      { value: 'inspections', label: 'Inspections' },
      { value: 'mobile', label: 'Mobile' },
    ],
  },
  {
    id: 'minTier',
    label: 'Min Tier',
    type: 'select',
    options: [
      { value: 'professional', label: 'Professional' },
      { value: 'advanced', label: 'Advanced' },
      { value: 'premium', label: 'Premium' },
    ],
  },
];

// ============================================================
// SEED TEMPLATES
// ============================================================

const SEED_TEMPLATES: GridTemplate[] = [
  {
    _id: 'feature-flags-default-template',
    gridType: 'feature-flags',
    name: 'Default View',
    description: 'All feature flags sorted by key.',
    state: { columnVisibility: {}, columnOrder: [], sorting: [{ id: 'key', desc: false }], filterValues: {}, pageSize: 25, searchText: '' },
    scope: 'system',
    createdBy: { userId: '507f1f77bcf86cd799439001', displayName: 'System' },
    isDefault: true, isPinned: false, usageCount: 0,
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    _id: 'feature-flags-enabled-template',
    gridType: 'feature-flags',
    name: 'Enabled Flags',
    description: 'Shows only currently enabled feature flags.',
    state: { columnVisibility: {}, columnOrder: [], sorting: [{ id: 'key', desc: false }], filterValues: { status: ['enabled'] }, pageSize: 25, searchText: '' },
    scope: 'system',
    createdBy: { userId: '507f1f77bcf86cd799439001', displayName: 'System' },
    isDefault: false, isPinned: false, usageCount: 0,
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
];

// ============================================================
// NewFlagModal
// ============================================================

interface NewFlagModalProps { onClose: () => void; onCreated: () => void; }

function NewFlagModal({ onClose, onCreated }: NewFlagModalProps) {
  const [form, setForm] = useState({ key: '', name: '', category: '', minTier: '', status: 'disabled', description: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      await createFeatureFlag({ key: form.key, name: form.name, category: form.category, description: form.description || undefined, minTier: form.minTier || null, status: form.status as 'enabled' | 'disabled' | 'beta', defaultValue: true });
      onCreated(); onClose();
    } catch (err: any) { setError(err.message || 'Failed to create flag'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg">
        <h2 className="text-lg font-semibold text-gray-900 mb-5">New Feature Flag</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Key <span className="text-red-500">*</span></label>
              <input type="text" required value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="feature.my-feature" />
              <p className="text-xs text-gray-400 mt-1">Use dot notation: category.feature-name</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
              <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="My Feature" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category <span className="text-red-500">*</span></label>
              <input type="text" required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="core" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Initial Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                <option value="disabled">Disabled</option><option value="enabled">Enabled</option><option value="beta">Beta</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Tier</label>
              <select value={form.minTier} onChange={(e) => setForm({ ...form, minTier: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                <option value="">All Tiers</option><option value="professional">Professional</option><option value="advanced">Advanced</option><option value="premium">Premium</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="Brief description of what this flag controls" />
            </div>
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50">{saving ? 'Creating...' : 'Create Flag'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// FeatureFlagsPage
// ============================================================

export default function FeatureFlagsPage() {
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [selectedFlag, setSelectedFlag] = useState<FeatureFlag | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [statusTarget, setStatusTarget] = useState<{ flag: FeatureFlag; newStatus: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<FeatureFlag | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isChangingStatus, setIsChangingStatus] = useState(false);

  const load = useCallback(() => {
    listFeatureFlags().then((data) => setFlags(data as FeatureFlag[])).catch(() => setFlags([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handlePanelSave = useCallback(async (fieldKey: string, value: unknown) => {
    if (!selectedFlag) return;
    const updated = { ...selectedFlag, [fieldKey]: value } as FeatureFlag;
    const prev = selectedFlag;
    setSelectedFlag(updated);
    setFlags((fs) => fs.map((f) => f._id === updated._id ? updated : f));
    try {
      await updateFeatureFlag(updated._id, updated);
    } catch {
      setSelectedFlag(prev);
      setFlags((fs) => fs.map((f) => f._id === prev._id ? prev : f));
    }
  }, [selectedFlag]);

  const handleConfirmStatusChange = async () => {
    if (!statusTarget) return;
    setIsChangingStatus(true);
    try {
      const updated = await updateFeatureFlagStatus(statusTarget.flag._id, statusTarget.newStatus);
      setFlags((fs) => fs.map((f) => f._id === updated._id ? updated : f));
      if (selectedFlag?._id === updated._id) setSelectedFlag(updated);
      setStatusTarget(null);
    } catch (err: any) {
      setActionError(err.message || 'Failed to update status');
      setStatusTarget(null);
    } finally {
      setIsChangingStatus(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteFeatureFlag(deleteTarget._id);
      setFlags((fs) => fs.filter((f) => f._id !== deleteTarget._id));
      if (selectedFlag?._id === deleteTarget._id) setSelectedFlag(null);
      setDeleteTarget(null);
    } catch (err: any) {
      setActionError(err.message || 'Failed to delete flag');
      setDeleteTarget(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const ROW_ACTIONS: DataGridRowAction<FeatureFlag>[] = [
    {
      id: 'view',
      label: 'View Details',
      icon: Eye,
      onClick: (row) => setSelectedFlag(row),
    },
    {
      id: 'enable',
      label: 'Enable',
      icon: CheckCircle2,
      onClick: (row) => setStatusTarget({ flag: row, newStatus: 'enabled' }),
      hidden: (row) => row.status === 'enabled',
    },
    {
      id: 'beta',
      label: 'Set to Beta',
      icon: FlaskConical,
      onClick: (row) => setStatusTarget({ flag: row, newStatus: 'beta' }),
      hidden: (row) => row.status === 'beta',
    },
    {
      id: 'disable',
      label: 'Disable',
      icon: XCircle,
      onClick: (row) => setStatusTarget({ flag: row, newStatus: 'disabled' }),
      hidden: (row) => row.status === 'disabled',
    },
    {
      id: 'delete',
      label: 'Delete Flag',
      icon: Trash2,
      variant: 'danger' as const,
      onClick: (row) => setDeleteTarget(row),
    },
  ];

  const statusVariant = statusTarget?.newStatus === 'disabled' ? 'warning' : 'default';
  const statusLabel = statusTarget
    ? `Set to ${statusTarget.newStatus.charAt(0).toUpperCase() + statusTarget.newStatus.slice(1)}`
    : '';

  return (
    <div className="flex flex-col h-full">
      {actionError && (
        <div className="mx-6 mt-3 flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 shrink-0">
          <span className="flex-1">{actionError}</span>
          <button onClick={() => setActionError(null)} className="text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      <div className="flex-1 min-h-0">
        <GridPageShell
          gridId="feature-flags"
          gridType="feature-flags"
          title="Feature Flags"
          subtitle="System-wide feature toggles and per-tenant overrides"
          columns={COLUMNS}
          data={flags}
          getRowId={(f) => f._id}
          filters={FILTERS}
          rowActions={ROW_ACTIONS}
          onRowClick={(f) => setSelectedFlag(f)}
          activeRowId={selectedFlag?._id ?? null}
          onPanelClose={() => setSelectedFlag(null)}
          panelTitle={selectedFlag?.name}
          panelSubtitle={selectedFlag?.key}
          panelDefaultWidth={520}
          panelContent={
            selectedFlag ? (
              <FlagDetailPanel
                flag={selectedFlag}
                onSave={handlePanelSave}
                onStatusChange={(flag, newStatus) => setStatusTarget({ flag, newStatus })}
              />
            ) : null
          }
          seedTemplates={SEED_TEMPLATES}
          onRefresh={load}
          emptyIcon={Flag}
          emptyTitle="No feature flags yet"
          emptySubtitle="Create your first feature flag to get started"
          emptyCTA={{ label: 'Add Flag', onClick: () => setShowNewModal(true) }}
          searchPlaceholder="Search flags by key or name..."
          toolbarActions={
            <button
              onClick={() => setShowNewModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors"
            >
              <Plus className="h-4 w-4" /> Add Flag
            </button>
          }
        />
      </div>

      {showNewModal && (
        <NewFlagModal
          onClose={() => setShowNewModal(false)}
          onCreated={load}
        />
      )}

      {statusTarget && (
        <ConfirmActionModal
          title={`${statusLabel}?`}
          description={<>Change <strong>{statusTarget.flag.name}</strong> status to <strong>{statusTarget.newStatus}</strong>?</>}
          confirmButtonLabel={statusLabel}
          variant={statusVariant}
          isSubmitting={isChangingStatus}
          onConfirm={handleConfirmStatusChange}
          onClose={() => setStatusTarget(null)}
        />
      )}

      {deleteTarget && (
        <ConfirmDeleteModal
          title="Delete Feature Flag?"
          confirmValue={deleteTarget.key}
          confirmLabel="flag key"
          description={<>Permanently delete <strong>{deleteTarget.name}</strong>? This cannot be undone.</>}
          deleteButtonLabel="Delete Flag"
          isSubmitting={isDeleting}
          onConfirm={handleConfirmDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

// ============================================================
// FlagDetailPanel
// ============================================================

interface FlagDetailPanelProps {
  flag: FeatureFlag;
  onSave: (fieldKey: string, value: unknown) => Promise<void>;
  onStatusChange: (flag: FeatureFlag, newStatus: string) => void;
}

function FlagDetailPanel({ flag, onSave, onStatusChange }: FlagDetailPanelProps) {
  const [activeTab, setActiveTab] = useState<'general' | 'evaluate' | 'audit'>('general');
  const [editing, setEditing] = useState<Record<string, boolean>>({});
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [fieldSaving, setFieldSaving] = useState<Record<string, boolean>>({});
  const [evalTenantId, setEvalTenantId] = useState('');
  const [evalResult, setEvalResult] = useState<any>(null);
  const [evaluating, setEvaluating] = useState(false);
  const [evalError, setEvalError] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);

  useEffect(() => {
    setEvalTenantId(''); setEvalResult(null); setEvalError(null);
    setEditing({}); setEditValues({});
  }, [flag._id]);

  const startEdit = (field: string, cur: string) => {
    setEditing((p) => ({ ...p, [field]: true }));
    setEditValues((p) => ({ ...p, [field]: cur }));
  };
  const cancelEdit = (field: string) => setEditing((p) => ({ ...p, [field]: false }));
  const saveField = async (field: string) => {
    setFieldSaving((p) => ({ ...p, [field]: true }));
    try { await onSave(field, editValues[field]); setEditing((p) => ({ ...p, [field]: false })); }
    finally { setFieldSaving((p) => ({ ...p, [field]: false })); }
  };

  const handleCopyKey = () => {
    navigator.clipboard.writeText(flag.key).then(() => { setCopiedKey(true); setTimeout(() => setCopiedKey(false), 1500); });
  };

  const handleEvaluate = async () => {
    if (!evalTenantId.trim()) return;
    setEvaluating(true); setEvalError(null);
    try { setEvalResult(await evaluateFeatureFlag(evalTenantId.trim())); }
    catch (err: any) { setEvalError(err.message || 'Evaluation failed'); setEvalResult(null); }
    finally { setEvaluating(false); }
  };

  const statusActions = (['enabled', 'disabled', 'beta'] as const).filter((s) => s !== flag.status);

  const EditableField = ({ field, label, value, multiline }: { field: string; label: string; value: string; multiline?: boolean }) => (
    <div>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      {editing[field] ? (
        <div className={multiline ? 'space-y-2' : 'flex gap-2'}>
          {multiline ? (
            <textarea autoFocus rows={3} value={editValues[field] ?? ''} onChange={(e) => setEditValues((p) => ({ ...p, [field]: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none" />
          ) : (
            <input autoFocus type="text" value={editValues[field] ?? ''} onChange={(e) => setEditValues((p) => ({ ...p, [field]: e.target.value }))}
              className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
          )}
          <div className={multiline ? 'flex gap-2' : 'contents'}>
            <button onClick={() => saveField(field)} disabled={fieldSaving[field]} className="px-3 py-1.5 text-xs font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50">
              {fieldSaving[field] ? '...' : 'Save'}
            </button>
            <button onClick={() => cancelEdit(field)} className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={() => startEdit(field, value)} className="w-full text-left px-3 py-2 text-sm text-gray-800 bg-gray-50 rounded-lg hover:bg-orange-50 hover:text-orange-800 transition-colors group">
          {value || <span className="text-gray-400 italic">Click to add {label.toLowerCase()}</span>}
          <span className="text-xs text-gray-400 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">edit</span>
        </button>
      )}
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <div className="flex gap-1 px-4 pt-3 pb-0 border-b border-gray-200 shrink-0">
        {(['general', 'evaluate', 'audit'] as const).map((t) => (
          <button key={t} onClick={() => setActiveTab(t)}
            className={`px-3 py-2 text-sm font-medium capitalize border-b-2 transition-colors -mb-px ${activeTab === t ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t === 'evaluate' ? 'Evaluate' : t === 'audit' ? 'Audit Log' : 'General'}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {activeTab === 'general' && (
          <>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Key</p>
              <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                <span className="font-mono text-sm text-gray-900 flex-1 truncate">{flag.key}</span>
                <button onClick={handleCopyKey} className="shrink-0 text-gray-400 hover:text-orange-600 transition-colors" title="Copy key">
                  {copiedKey ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Status</p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full capitalize ${STATUS_COLORS[flag.status] || 'bg-gray-100 text-gray-600'}`}>{flag.status}</span>
                {statusActions.map((s) => (
                  <button key={s} onClick={() => onStatusChange(flag, s)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border transition-colors ${s === 'enabled' ? 'border-green-200 text-green-700 hover:bg-green-50' : s === 'disabled' ? 'border-gray-200 text-gray-600 hover:bg-gray-50' : 'border-yellow-200 text-yellow-700 hover:bg-yellow-50'}`}>
                    {s === 'enabled' ? <CheckCircle2 className="h-3 w-3" /> : s === 'disabled' ? <XCircle className="h-3 w-3" /> : <FlaskConical className="h-3 w-3" />}
                    Set {s}
                  </button>
                ))}
              </div>
            </div>

            <EditableField field="name" label="Name" value={flag.name} />
            <EditableField field="category" label="Category" value={flag.category} />
            <EditableField field="description" label="Description" value={flag.description || ''} multiline />

            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Min Tier</p>
              {editing['minTier'] ? (
                <div className="flex gap-2">
                  <select autoFocus value={editValues['minTier'] ?? ''} onChange={(e) => setEditValues((p) => ({ ...p, minTier: e.target.value }))}
                    className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                    <option value="">All Tiers</option>
                    <option value="professional">Professional</option>
                    <option value="advanced">Advanced</option>
                    <option value="premium">Premium</option>
                  </select>
                  <button onClick={() => saveField('minTier')} disabled={fieldSaving['minTier']} className="px-3 py-1.5 text-xs font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50">{fieldSaving['minTier'] ? '...' : 'Save'}</button>
                  <button onClick={() => cancelEdit('minTier')} className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                </div>
              ) : (
                <button onClick={() => startEdit('minTier', flag.minTier || '')} className="w-full text-left px-3 py-2 text-sm text-gray-800 bg-gray-50 rounded-lg hover:bg-orange-50 hover:text-orange-800 transition-colors group">
                  {flag.minTier
                    ? <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full capitalize ${TIER_COLORS[flag.minTier] || 'bg-gray-100 text-gray-600'}`}>{flag.minTier}</span>
                    : <span className="text-gray-400">All Tiers</span>}
                  <span className="text-xs text-gray-400 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">edit</span>
                </button>
              )}
            </div>

            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Default Value</p>
              <div className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg">
                <span className={`text-sm font-medium ${flag.defaultValue ? 'text-green-600' : 'text-gray-500'}`}>{flag.defaultValue ? '✓ On' : '✗ Off'}</span>
                <button onClick={() => onSave('defaultValue', !flag.defaultValue)} className="text-xs text-gray-400 hover:text-orange-600 underline transition-colors">toggle</button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-1 border-t border-gray-100">
              {flag.createdAt && (
                <div><p className="text-xs text-gray-400">Created</p><p className="text-xs text-gray-600">{new Date(flag.createdAt).toLocaleDateString()}</p></div>
              )}
              {flag.updatedAt && (
                <div><p className="text-xs text-gray-400">Last Updated</p><p className="text-xs text-gray-600">{new Date(flag.updatedAt).toLocaleDateString()}</p></div>
              )}
            </div>
          </>
        )}

        {activeTab === 'audit' && (
          <AuditLogTimeline
            documentId={flag._id}
            collection="featureFlags"
            theme={{ cardBg: '', cardBorder: '', accent: '', accentText: '', textPrimary: '', textSecondary: '', expandedBg: '' } as any}
            active={activeTab === 'audit'}
            fetchFn={fetchAuditLogs}
          />
        )}

        {activeTab === 'evaluate' && (
          <>
            <div>
              <p className="text-sm text-gray-600 mb-3">Enter a tenant ID to evaluate all feature flags for that tenant.</p>
              <div className="flex gap-2">
                <input type="text" value={evalTenantId} onChange={(e) => setEvalTenantId(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleEvaluate()}
                  placeholder="Paste a Tenant ID"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
                <button onClick={handleEvaluate} disabled={evaluating || !evalTenantId.trim()}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 disabled:opacity-50">
                  {evaluating ? '...' : 'Evaluate'}
                </button>
              </div>
              {evalError && <p className="mt-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{evalError}</p>}
            </div>

            {evalResult && (
              <div>
                <div className="text-sm text-gray-500 mb-3">
                  Tenant: <span className="font-mono text-gray-800">{evalResult.tenantId}</span>
                  {evalResult.tier && <> — Tier: <span className="font-medium capitalize text-gray-800">{evalResult.tier}</span></>}
                </div>
                <div className="rounded-lg border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Flag</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Enabled</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Source</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {evalResult.flags?.map((f: any) => (
                        <tr key={f.key}>
                          <td className="px-3 py-2 font-mono text-xs text-gray-700">{f.key}</td>
                          <td className="px-3 py-2"><span className={f.enabled ? 'text-green-600 font-medium' : 'text-gray-400'}>{f.enabled ? '✓ ON' : '✗ OFF'}</span></td>
                          <td className="px-3 py-2 text-gray-500 text-xs">{f.source}{f.overrideReason ? ` — ${f.overrideReason}` : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
